import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog from "@saleor/components/SaveFilterTabDialog";
import CouponCollectionList from "@saleor/coupons/components/CouponCollectionList/CouponCollectionList";
import { FeaturedInitialFormData } from "@saleor/coupons/components/CouponFeaturedStar";
import { useCouponCollectionToggleIsFeatured } from "@saleor/coupons/mutations";
import { useCouponCollectionListQuery } from "@saleor/coupons/queries";
import {
  couponCollectionListUrl,
  CouponCollectionListUrlFilters,
  CouponCollectionListUrlQueryParams,
  couponCollectionsUrl,
  couponCollectionViewPath
} from "@saleor/coupons/url";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import { maybe } from "@saleor/misc";
import { ListViews } from "@saleor/types";
import {
  CouponCollectionSortField,
  OrderDirection
} from "@saleor/types/globalTypes";
import React, { useMemo } from "react";

import { getActiveFilters, getFilterVariables } from "./filters";

interface CouponCollectionListPageProps {
  params: CouponCollectionListUrlQueryParams;
}

export const CouponCollectionListPage: React.FC<CouponCollectionListPageProps> = ({
  params
}) => {
  const paginate = usePaginator();
  const navigate = useNavigator();
  const notify = useNotifier();
  const showTabs = false;
  const couponCollectionTabs = [
    {
      name: "Mall Deals"
    },
    {
      name: "Mall Events"
    },
    {
      name: "Mall Deals & Events"
    }
  ];

  const { settings, updateListSettings } = useListSettings(
    ListViews.COUPON_COLLECTION_LIST
  );
  const sort = {
    sortBy: {
      direction: OrderDirection.DESC,
      field: CouponCollectionSortField.UPDATED_AT
    }
  };
  const paginationState = createPaginationState(settings?.rowNumber, params);
  const queryVariables = useMemo(
    () => ({
      ...paginationState,
      ...sort,
      filter: {
        ...getFilterVariables(params)
      }
    }),
    [paginationState, params, settings?.rowNumber]
  );

  const changeFilterField = (filter: CouponCollectionListUrlFilters) => {
    navigate(
      couponCollectionListUrl({
        ...getActiveFilters(params),
        ...filter
      })
    );
  };

  const { data, loading, refetch } = useCouponCollectionListQuery({
    displayLoader: true,
    variables: queryVariables
  });

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.couponCollections.pageInfo),
    paginationState,
    params
  );

  const [
    updateCouponCollectionToggleIsFeatured
  ] = useCouponCollectionToggleIsFeatured({
    onCompleted: event => {
      const { errors } = event.couponCollectionToggleIsFeatured;
      if (errors.length > 0) {
        notify({ status: "error", text: errors[0].message });
      }

      if (params.after || params.before) {
        // PRDV-14105: bugfix for setting featuring coupon collections on 2nd page and beyond of the listing table
        navigate(couponCollectionsUrl());
      } else {
        refetch();
      }
    }
  });

  const handleCouponCollectionToggleIsFeatured = async (
    data: FeaturedInitialFormData
  ) => {
    updateCouponCollectionToggleIsFeatured({ variables: { id: data.id } });
    return null;
  };

  return (
    <>
      <CouponCollectionList
        data={maybe(
          () => data?.couponCollections.edges.map(edge => edge.node),
          []
        )}
        currentTab={0}
        tabs={showTabs ? couponCollectionTabs.map(tab => tab.name) : ["All"]}
        onTabChange={function(tab: number): void {
          throw new Error(`"Function not implemented." ${tab}`);
        }}
        onTabDelete={function(): void {
          throw new Error("Function not implemented.");
        }}
        onTabSave={function(): void {
          throw new Error("Function not implemented.");
        }}
        onAdd={function(): void {
          throw new Error("Function not implemented.");
        }}
        pageInfo={pageInfo}
        settings={settings}
        disabled={loading}
        initialSearch={params.query || ""}
        onSearchChange={query => changeFilterField({ query })}
        onUpdateListSettings={updateListSettings}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onRowClick={id => () => navigate(couponCollectionViewPath(id))}
        onSubmit={handleCouponCollectionToggleIsFeatured}
      />
      <SaveFilterTabDialog
        open={undefined}
        confirmButtonState="default"
        onClose={undefined}
        onSubmit={undefined}
      />
      <DeleteFilterTabDialog
        open={undefined}
        confirmButtonState="default"
        onClose={undefined}
        onSubmit={undefined}
        tabName={undefined}
      />
    </>
  );
};
export default CouponCollectionListPage;
